<script setup>
/* global axios */
// Email Marketing — tela principal com 4 abas: Campanhas, Templates, Credenciais, Supressão.
// Fase 1: lista + CRUD de credenciais/supressão/templates. Wizard e editor em fase seguinte.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const TABS = ['campaigns', 'templates', 'credentials', 'suppression'];
const tab = ref('campaigns');
const loading = ref(true);
const campaigns = ref([]);
const templates = ref([]);
const credentials = ref([]);
const suppression = ref([]);

const STATUS = {
  draft: 'bg-n-alpha-2 text-n-slate-11',
  scheduled: 'bg-n-blue-3 text-n-blue-11',
  running: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-iris-3 text-n-iris-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};

const PROVIDERS = [
  { value: 'smtp', label: 'SMTP' },
  { value: 'resend', label: 'Resend' },
  { value: 'sendgrid', label: 'SendGrid' },
  { value: 'amazon_ses', label: 'Amazon SES' },
  { value: 'brevo', label: 'Brevo' },
];
const SES_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'sa-east-1',
  'eu-west-1',
  'eu-central-1',
  'ap-southeast-1',
];
const apiKeyPlaceholder = 'sk-...';
const freshCred = () => ({
  provider: 'smtp',
  from_email: '',
  from_name: '',
  reply_to: '',
  config: {
    host: '',
    port: 587,
    user: '',
    password: '',
    secure: true,
    api_key: '',
    access_key_id: '',
    secret_access_key: '',
    region: 'us-east-1',
  },
});
const showCred = ref(false);
const credForm = ref(freshCred());
const suppForm = ref({ emails: '', reason: '' });

const loadTab = async key => {
  loading.value = true;
  try {
    if (key === 'campaigns')
      campaigns.value =
        (await axios.get(`${base}/email_campaigns`)).data.data || [];
    else if (key === 'templates')
      templates.value =
        (await axios.get(`${base}/email_templates`)).data.data || [];
    else if (key === 'credentials')
      credentials.value =
        (await axios.get(`${base}/email_credentials`)).data.data || [];
    else
      suppression.value =
        (await axios.get(`${base}/email_suppressions`)).data.data || [];
  } finally {
    loading.value = false;
  }
};
const switchTab = key => {
  tab.value = key;
  loadTab(key);
};

const campAct = async (c, action) => {
  await axios.post(`${base}/email_campaigns/${c.id}/${action}`);
  await loadTab('campaigns');
};
const campDelete = async c => {
  if (!window.confirm(t('EMAIL.confirmDelete', { name: c.name }))) return;
  await axios.delete(`${base}/email_campaigns/${c.id}`);
  await loadTab('campaigns');
};
const openReport = c =>
  router.push({
    name: 'kanban_email_report',
    params: { accountId, campaignId: c.id },
  });
const newCampaign = () =>
  router.push({ name: 'kanban_email_wizard', params: { accountId } });

const newTemplate = () =>
  router.push({ name: 'kanban_email_template_new', params: { accountId } });
const editTemplate = tpl =>
  router.push({
    name: 'kanban_email_template',
    params: { accountId, templateId: tpl.id },
  });
const deleteTemplate = async tpl => {
  if (!window.confirm(t('EMAIL.confirmDelete', { name: tpl.name }))) return;
  await axios.delete(`${base}/email_templates/${tpl.id}`);
  await loadTab('templates');
};
const duplicateTemplate = async tpl => {
  await axios.post(`${base}/email_templates/${tpl.id}/duplicate`);
  await loadTab('templates');
};

const saveCred = async () => {
  if (!credForm.value.from_email.trim()) return;
  await axios.post(`${base}/email_credentials`, credForm.value);
  showCred.value = false;
  credForm.value = freshCred();
  await loadTab('credentials');
};
const activateCred = async c => {
  await axios.patch(`${base}/email_credentials/${c.id}/activate`);
  await loadTab('credentials');
};
const testCred = async c => {
  const r = (await axios.post(`${base}/email_credentials/${c.id}/test`, {}))
    .data;
  window.alert(r.ok ? t('EMAIL.verified') : r.error);
  await loadTab('credentials');
};
const deleteCred = async c => {
  await axios.delete(`${base}/email_credentials/${c.id}`);
  await loadTab('credentials');
};

const addSupp = async () => {
  if (!suppForm.value.emails.trim()) return;
  await axios.post(`${base}/email_suppressions`, suppForm.value);
  suppForm.value = { emails: '', reason: '' };
  await loadTab('suppression');
};
const delSupp = async s => {
  await axios.delete(`${base}/email_suppressions/${s.id}`);
  await loadTab('suppression');
};

const TAB_ICONS = {
  campaigns:
    'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  templates:
    'M4 5a1 1 0 011-1h14a1 1 0 011 1v4H4V5zm0 6h7v9H5a1 1 0 01-1-1v-8zm9 0h7v8a1 1 0 01-1 1h-6v-9z',
  credentials:
    'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  suppression:
    'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728A9 9 0 015.636 5.636',
};

onMounted(() => loadTab('campaigns'));
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header class="flex items-center gap-3 px-6 py-4 border-b border-n-weak">
      <div
        class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-brand"
      >
        <svg
          class="w-4 h-4 text-white"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </div>
      <h1 class="text-heading-1 text-n-slate-12">
        {{ t('EMAIL.title') }}
      </h1>
    </header>

    <div class="flex gap-1 px-6 pt-3 border-b border-n-weak">
      <button
        v-for="tb in TABS"
        :key="tb"
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 border-transparent text-n-slate-10 hover:text-n-slate-12"
        :class="{ '!text-n-brand !border-n-brand': tab === tb }"
        @click="switchTab(tb)"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="TAB_ICONS[tb]"
          />
        </svg>
        {{ t(`EMAIL.tabs.${tb}`) }}
      </button>
    </div>

    <div class="flex-1 p-6 overflow-y-auto">
      <div class="flex items-center mb-4">
        <p class="text-sm text-n-slate-11">{{ t('EMAIL.subtitle') }}</p>
        <button
          v-if="tab === 'campaigns'"
          class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
          @click="newCampaign"
        >
          + {{ t('EMAIL.newCampaign') }}
        </button>
        <button
          v-else-if="tab === 'templates'"
          class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
          @click="newTemplate"
        >
          + {{ t('EMAIL.newTemplate') }}
        </button>
        <button
          v-else-if="tab === 'credentials'"
          class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
          @click="showCred = true"
        >
          + {{ t('EMAIL.addCredential') }}
        </button>
      </div>

      <div v-if="loading" class="text-sm text-n-slate-10">
        {{ t('EMAIL.loading') }}
      </div>

      <!-- campanhas -->
      <template v-else-if="tab === 'campaigns'">
        <p
          v-if="!campaigns.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('EMAIL.empty') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10 border-b border-n-weak">
              <th class="px-3 py-2 font-medium">{{ t('EMAIL.col.name') }}</th>
              <th class="px-3 py-2 font-medium">{{ t('EMAIL.col.status') }}</th>
              <th class="px-3 py-2 font-medium">
                {{ t('EMAIL.col.contacts') }}
              </th>
              <th class="px-3 py-2 font-medium">{{ t('EMAIL.col.sent') }}</th>
              <th class="px-3 py-2 font-medium">{{ t('EMAIL.col.opens') }}</th>
              <th class="px-3 py-2 font-medium text-right">
                {{ t('EMAIL.col.actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in campaigns"
              :key="c.id"
              class="border-b border-n-weak hover:bg-n-alpha-1"
            >
              <td
                class="px-3 py-2.5 font-medium cursor-pointer text-n-slate-12"
                @click="openReport(c)"
              >
                {{ c.name }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                  :class="STATUS[c.status]"
                  >{{ t(`EMAIL.status.${c.status}`) }}</span
                >
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ c.total_contacts }}
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">{{ c.sent_count }}</td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ c.open_rate != null ? `${c.open_rate}%` : '—' }}
              </td>
              <td class="px-3 py-2.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    v-if="['draft', 'scheduled', 'paused'].includes(c.status)"
                    class="p-1.5 rounded-md text-n-teal-11 hover:bg-n-alpha-1"
                    :title="t('EMAIL.act.start')"
                    @click="campAct(c, 'launch')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                  <button
                    v-if="c.status === 'running'"
                    class="p-1.5 rounded-md text-n-amber-11 hover:bg-n-alpha-1"
                    :title="t('EMAIL.act.pause')"
                    @click="campAct(c, 'pause')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                    </svg>
                  </button>
                  <button
                    v-if="['running', 'paused'].includes(c.status)"
                    class="p-1.5 rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
                    :title="t('EMAIL.act.cancel')"
                    @click="campAct(c, 'cancel')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                  <button
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
                    :title="t('EMAIL.act.report')"
                    @click="openReport(c)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 19v-6m4 6V5m4 14v-9M5 19h14"
                      />
                    </svg>
                  </button>
                  <button
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
                    :title="t('EMAIL.act.duplicate')"
                    @click="campAct(c, 'duplicate')"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 8V6a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-2M6 20h8a2 2 0 002-2v-8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    v-if="c.status !== 'running'"
                    class="p-1.5 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                    :title="t('EMAIL.delete')"
                    @click="campDelete(c)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- templates -->
      <template v-else-if="tab === 'templates'">
        <p
          v-if="!templates.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('EMAIL.emptyTemplates') }}
        </p>
        <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div
            v-for="tpl in templates"
            :key="tpl.id"
            class="flex flex-col p-4 border rounded-lg border-n-weak"
          >
            <div class="font-medium text-n-slate-12">{{ tpl.name }}</div>
            <div class="text-xs truncate text-n-slate-10">
              {{ tpl.subject }}
            </div>
            <div class="flex gap-1 mt-3">
              <button
                class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
                @click="editTemplate(tpl)"
              >
                {{ t('EMAIL.act.edit') }}
              </button>
              <button
                class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
                @click="duplicateTemplate(tpl)"
              >
                {{ t('EMAIL.act.duplicate') }}
              </button>
              <button
                class="px-2.5 py-1 text-xs rounded-md border border-n-ruby-6 text-n-ruby-11 hover:bg-n-ruby-3"
                @click="deleteTemplate(tpl)"
              >
                {{ t('EMAIL.delete') }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- credenciais -->
      <template v-else-if="tab === 'credentials'">
        <p
          v-if="!credentials.length"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('EMAIL.emptyCreds') }}
        </p>
        <div
          v-for="c in credentials"
          :key="c.id"
          class="flex items-center gap-3 px-3 py-3 mb-2 border rounded-lg border-n-weak"
        >
          <span
            class="px-2 py-0.5 text-[0.625rem] uppercase rounded-full bg-n-alpha-2 text-n-slate-11"
            >{{ c.provider }}</span
          >
          <div class="min-w-0">
            <div class="font-medium truncate text-n-slate-12">
              {{ c.from_name }} &lt;{{ c.from_email }}&gt;
            </div>
            <div class="text-[0.6875rem] text-n-slate-10">{{ c.host }}</div>
          </div>
          <span
            v-if="c.is_verified"
            class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-teal-3 text-n-teal-11"
            >{{ t('EMAIL.verified') }}</span
          >
          <span
            v-if="c.is_active"
            class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-blue-3 text-n-blue-11"
            >{{ t('EMAIL.active') }}</span
          >
          <div class="flex gap-1 ml-auto">
            <button
              v-if="!c.is_active"
              class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
              @click="activateCred(c)"
            >
              {{ t('EMAIL.act.activate') }}
            </button>
            <button
              class="px-2.5 py-1 text-xs rounded-md border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
              @click="testCred(c)"
            >
              {{ t('EMAIL.act.test') }}
            </button>
            <button
              class="px-2.5 py-1 text-xs rounded-md border border-n-ruby-6 text-n-ruby-11 hover:bg-n-ruby-3"
              @click="deleteCred(c)"
            >
              {{ t('EMAIL.delete') }}
            </button>
          </div>
        </div>
      </template>

      <!-- supressão -->
      <template v-else>
        <div class="flex max-w-2xl gap-2 mb-3">
          <input
            v-model="suppForm.emails"
            class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('EMAIL.supp.emailsPlaceholder')"
          />
          <button
            class="px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
            :disabled="!suppForm.emails.trim()"
            @click="addSupp"
          >
            {{ t('EMAIL.addEmail') }}
          </button>
        </div>
        <p
          v-if="!suppression.length"
          class="py-6 text-sm text-center text-n-slate-10"
        >
          {{ t('EMAIL.emptySupp') }}
        </p>
        <div
          v-for="s in suppression"
          :key="s.id"
          class="flex items-center gap-2 px-3 py-2 mb-1 border rounded-lg border-n-weak max-w-2xl"
        >
          <span class="font-medium text-n-slate-12">{{ s.email }}</span>
          <template v-if="s.reason">
            <span class="text-xs text-n-slate-10">— {{ s.reason }}</span>
          </template>
          <button
            class="p-1 ml-auto rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
            @click="delSupp(s)"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </template>
    </div>

    <!-- modal credencial -->
    <div
      v-if="showCred"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showCred = false"
    >
      <div
        class="w-[28rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('EMAIL.cred.title') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="showCred = false"
          >
            <svg
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.cred.provider')
            }}</label>
            <select
              v-model="credForm.provider"
              class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            >
              <option v-for="pv in PROVIDERS" :key="pv.value" :value="pv.value">
                {{ pv.label }}
              </option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.fromEmail')
              }}</label
              ><input
                v-model="credForm.from_email"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.fromName')
              }}</label
              ><input
                v-model="credForm.from_name"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>

          <!-- SMTP -->
          <div
            v-if="credForm.provider === 'smtp'"
            class="grid grid-cols-2 gap-3"
          >
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.host')
              }}</label
              ><input
                v-model="credForm.config.host"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.port')
              }}</label
              ><input
                v-model.number="credForm.config.port"
                type="number"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.user')
              }}</label
              ><input
                v-model="credForm.config.user"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.password')
              }}</label
              ><input
                v-model="credForm.config.password"
                type="password"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <label
              class="flex items-center col-span-2 gap-2 text-sm text-n-slate-11"
              ><input v-model="credForm.config.secure" type="checkbox" />
              {{ t('EMAIL.cred.secure') }}</label
            >
          </div>

          <!-- Amazon SES -->
          <div
            v-else-if="credForm.provider === 'amazon_ses'"
            class="grid grid-cols-2 gap-3"
          >
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.awsAccessKey')
              }}</label
              ><input
                v-model="credForm.config.access_key_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.awsSecretKey')
              }}</label
              ><input
                v-model="credForm.config.secret_access_key"
                type="password"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div class="col-span-2">
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('EMAIL.cred.awsRegion')
              }}</label>
              <select
                v-model="credForm.config.region"
                class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              >
                <option v-for="r in SES_REGIONS" :key="r" :value="r">
                  {{ r }}
                </option>
              </select>
            </div>
          </div>

          <!-- Resend / SendGrid / Brevo -->
          <div v-else>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.cred.apiKey')
            }}</label>
            <input
              v-model="credForm.config.api_key"
              type="password"
              :placeholder="apiKeyPlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            />
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('EMAIL.cred.replyTo')
            }}</label>
            <input
              v-model="credForm.reply_to"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            />
          </div>
          <div class="flex justify-end gap-2">
            <button
              class="px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="showCred = false"
            >
              {{ t('EMAIL.cancel') }}
            </button>
            <button
              class="px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
              :disabled="!credForm.from_email.trim()"
              @click="saveCred"
            >
              {{ t('EMAIL.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
